import { Injectable, OnDestroy } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { RestService } from '../rest/rest.service';
import { environment } from '../../../environments/environment';
import { Observable, of, Subscription } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as fromApp from '../../app.reducer';
import { User } from '../user/user.service';
import { HANDLE_404_LOCALLY } from '../error/error.tokens';

export const LATEST_LISTING_KEY = 'edit_listing_latest';

export interface ReportRequest {
	reason: 'duplicate' | 'inappropriate' | 'miscategorized' | 'prohibited' | 'scam' | 'other';
	message: string;
}

@Injectable({
	providedIn: 'root',
})
export class ListingService implements OnDestroy {
	public packages: any[] = [];
	private userSubscription: Subscription;
	private user: User;

	constructor(
		private rest: RestService,
		private store: Store<fromApp.AppState>
	) {
		this.userSubscription = this.store
			.select('user')
			.pipe(map((e) => e.user))
			.subscribe((user: User) => {
				if (!this.user && user) {
					console.log('should attempt to pull the packages list');
					this.getListingPackages().subscribe({
						next: () => console.log('packages loaded on user login'),
						error: (err) => console.error('failed to load packages on login:', err)
					});
				} else if (this.user && !user) {
					this.packages = [];
				}
				this.user = user ? user : null;
			});
	}

	ngOnDestroy(): void {
		if (this.userSubscription) {
			this.userSubscription.unsubscribe();
		}
	}

	deleteListing(type: string, pk: string): Observable<any> {
		console.log('deleteListing()');
		return this.rest.delete(environment.base_api_url + '/' + type + '_listings/' + pk + '/');
	}

	getListing(type: string, pk: string): Observable<any> {
		const context = new HttpContext().set(HANDLE_404_LOCALLY, true);
		return this.rest.get(environment.base_api_url + '/' + type + '_listings/' + pk + '/', context);
	}

	getUserListings(userPk: string, nextPageUrl?: string, status = ''): Observable<any> {
		let url = environment.base_api_url + '/auth/users/' + userPk + '/listings/?status=' + status + '&type=horse';
		if (nextPageUrl) {
			url = nextPageUrl;
		}
		console.log('getUserListingsSelling url:', url);
		return this.rest.get(url);
	}

	getListingMedia(type: string, pk: string): Observable<any> {
		const context = new HttpContext().set(HANDLE_404_LOCALLY, true);
		return this.rest.get(environment.base_api_url + '/' + type + '_listings/' + pk + '/media/', context);
	}

	//   public get current_listing():any {
	//     return this._current_listing;
	//   }

	//   public set current_listing(data) {
	//     this._current_listing = data;
	//   }

	// public saveListing(data:any): Observable<any> {
	//   let url = environment.base_api_url+'/'+listing_type+'_listings/'+pk+'/media/'
	//   console.log('saveListing url:', url);
	//   return this.rest.post(url, data)
	// }

	public addListing(listing: any, type: string): Observable<any> {
		let url = environment.base_api_url + '/horse_listings/';
		if (type === 'tack') {
			url = environment.base_api_url + '/tack_listings/';
		}
		return this.rest.post(url, listing);
	}

	public patchListing(listing: any, payload: any): Observable<any> {
		console.log('patchListing()');
		const url = environment.base_api_url + '/' + listing.type + '_listings/' + listing.pk + '/';
		return this.rest.patch(url, payload);
	}

	public addNewMedia(media: any): Observable<any> {
		console.log('addNewMedia()');
		console.log(media);
		return this.rest.post(environment.base_api_url + '/listing_media/', media).pipe(
			tap((returnedData) => {
				console.log('addNewMedia:');
				console.log(returnedData);
			})
		);
	}

	public linkMedia(mediaPk, listingPk, type) {
		const url = environment.base_api_url + '/' + type + '_listings/' + listingPk + '/add_media/';
		return this.rest.post(url, {
			media: mediaPk,
		});
	}

	/**
	 * Remove/unlink a media item from a listing (does not necessarily delete the media record).
	 * Mirrors the backend "add_media" action pattern.
	 */
	public removeMediaFromListing(type: string, listingPk: string, mediaPk: string): Observable<any> {
		const url = environment.base_api_url + '/' + type + '_listings/' + listingPk + '/remove_media/';
		return this.rest.post(url, { media: mediaPk });
	}

	public getBaseProduct(listing) {
		if (!listing.type) {
			return false;
		}
		const append = '_single';
		const targetSlug = listing.type + append;
		console.log('this.packages:');
		console.log(this.packages);

		if (this.packages && this.packages.length) {
			const pos = this.packages.map((e) => e.code).indexOf(targetSlug);
			if (pos > -1) {
				return this.packages[pos];
			}
		}
		return false;
	}

	public getListingPackages(): Observable<any[]> {
		console.log('getListingPackages()');
		return this.rest.get(environment.base_api_url + '/products/').pipe(
			tap((products: any[]) => {
				console.log('looked up our products');
				console.log(products);
				if (products && products.hasOwnProperty('length')) {
					// Clear and repopulate packages array
					this.packages = [];
					// eslint-disable-next-line @typescript-eslint/prefer-for-of
					for (let i = 0; i < products.length; i++) {
						this.packages.push(products[i]);
					}
					console.log('finished loading getListingPackages');
				}
			})
		);
	}

	/**
	 * Ensures products are loaded, waits if necessary
	 * Returns Promise<boolean> indicating success
	 */
	public ensurePackagesLoaded(): Promise<boolean> {
		// Already loaded
		if (this.packages && this.packages.length > 0) {
			console.log('packages already loaded');
			return Promise.resolve(true);
		}

		console.log('packages not loaded, fetching...');
		
		// Race between loading products and timeout
		return new Promise((resolve) => {
			const timeout = setTimeout(() => {
				console.error('Product loading timed out after 10s');
				resolve(false);
			}, 10000); // 10 second timeout

			this.getListingPackages().subscribe({
				next: (products) => {
					clearTimeout(timeout);
					resolve(products && products.length > 0);
				},
				error: (err) => {
					console.error('Failed to load products:', err);
					clearTimeout(timeout);
					resolve(false);
				}
			});
		});
	}

	public requestPackage(serializedPurchase, listing, baseProduct): Observable<any> {
		return this.rest.post(environment.base_api_url + '/purchases/', serializedPurchase).pipe(
			map((data) => {
				console.log('addBaseProductType doAuthPost /purchases');
				console.log(data);
				return data;
			}),
			switchMap((data) => {
				if (data && data.pk) {
					console.log('doing apply on the purchase...');
					return this.rest.post(environment.base_api_url + '/' + listing.type + '_listings/' + listing.pk + '/apply_purchase/', {
						purchase: data.pk,
					});
				} else {
					return of(data);
				}
			})
		);
		// .subscribe( (data)=> {
		//   if(data && data['pk']){
		//     console.log('completed base level purchase for user: ');
		//     console.log(data);
		//     // if(baseProduct && baseProduct['data']){
		//     //   let limits = baseProduct['data'];
		//     //   if(limits && limits['photos']){
		//     //     console.log('photo limit is: ' + limits['photos']);
		//     //     this.limits['photoLimit'] = limits['photos'];
		//     //   }
		//     //   if(limits && limits['videos']){
		//     //     console.log('video limit is: ' + limits['videos']);
		//     //     this.limits['videoLimit'] = limits['videos'];
		//     //   }
		//     //   console.log('did try updating the limits:');
		//     //   console.log(this.limits);

		//     // }
		//     // let mediaLimits = baseProduct;
		//     // resolve(data);
		//     return data;
		//   }else{
		//     console.log('failed to complete the base package save');
		//     // reject(data);
		//     return data;
		//   }
		// })
	}

	public removeListingMedia(mediaPk: string): Observable<any> {
		return this.rest.delete(environment.base_api_url + '/listing_media/' + mediaPk + '/');
	}

	public publishListing(listing: any): Observable<any> {
		return this.rest.post(environment.base_api_url + '/' + listing.type + '_listings/' + listing.pk + '/publish/', listing);
	}

	public setCoverImage(listing_type: string, listing_pk: string, media_pk: string): Observable<any> {
		return this.rest.post(environment.base_api_url + '/' + listing_type + '_listings/' + listing_pk + '/set_media_order/', {
			media: media_pk,
			order: 0,
		});
	}

	public markSold(listing: any, buyerDetails?: any): Observable<any> {
		/* 
		*	{
				"buyer": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
				"buyer_info": {},
				"date": "2025-02-24",
				"price": "string",
				"data": {}
			}
		*/
		const payload = {
			buyer: buyerDetails?.buyer ? buyerDetails.buyer : null,
			buyer_info: buyerDetails?.buyer_info ? buyerDetails.buyer_info : {},
			date: new Date().toISOString().split('T')[0],
			price: buyerDetails?.price ? buyerDetails.price : null,
			data: buyerDetails?.data ? buyerDetails.data : {},
		};
		return this.rest.post(environment.base_api_url + '/' + listing.type + '_listings/' + listing.pk + '/report_sale/', payload);
	}

	/**
	 * Records a view of a listing for analytics. Fire-and-forget from the caller's
	 * perspective; the backend de-duplicates/attributes as needed. Owner views are
	 * excluded by the caller, not here.
	 */
	public recordView(type: string, pk: string): Observable<any> {
		return this.rest.post(environment.base_api_url + '/' + type + '_listings/' + pk + '/record_view/', {});
	}

	public reportListing(pk: string, type: string, report: ReportRequest): Observable<any> {
		console.log('reportListing()');
		/*
		// horse_listings/{pk}/report/
		{
			"reason": "duplicate", // [ duplicate, inappropriate, miscategorized, prohibited, scam, other ]
			"message": "string"
			}
		*/
		return this.rest.post(environment.base_api_url + '/' + type + '_listings/' + pk + '/report/', report);
	}
}
